/**
 * Helper sync para renderear un QR como string SVG.
 *
 * Usa `qrcode.create()` (sync) — devuelve la matriz de módulos — y
 * arma el SVG manualmente con la estrategia de la lib `qrcode` para
 * SVG output: un solo <path> con strokes horizontales por fila.
 * Esto produce un SVG ~14x más liviano que `qrcode-svg` (lib vieja
 * que usábamos antes y que generaba SVGs que iPhone no escaneaba en
 * tamaños chicos por subpixel rendering).
 *
 * Strategy:
 *   - Iteramos cada row del QR matrix
 *   - Encontramos runs contiguos de módulos negros (1)
 *   - Cada run = `M{x} {y+0.5} h{len}` — una linea horizontal con
 *     stroke-width 1 centrada en y+0.5 (alineada a pixel boundary)
 *   - Todos los runs van en un solo <path> con stroke negro
 *   - El SVG usa `viewBox` en unidades de módulos → escala
 *     perfectamente al tamaño CSS sin ningún issue de subpixel
 *
 * El resultado escanea con cualquier reader (cámara nativa iOS/Android
 * incluida) porque:
 *   1. La geometría es estricta — cada módulo es exactamente 1×1 en
 *      el viewBox, sin sub-pixel offsets
 *   2. crispEdges en el SVG root → renderizado pixel-perfect
 *   3. Quiet zone reglamentaria (margin: 4 módulos)
 */
import QRCode from 'qrcode'

export type QrEcl = 'L' | 'M' | 'Q' | 'H'

export interface QrOptions {
  /** Texto / URL a codificar */
  content: string
  /** Error Correction Level. M = 15% (default · balance estándar). */
  ecl?: QrEcl
  /** Quiet zone en módulos. Default 4 (spec QR). */
  margin?: number
  /** Tamaño final en px del SVG renderizado (width=height). Default 120. */
  size?: number
  /** Color de los módulos. Default `#000000`. */
  color?: string
  /** Color de fondo. Default `#ffffff`. */
  background?: string
}

export function renderQrSvg(opts: QrOptions): string {
  const ecl = opts.ecl ?? 'M'
  const margin = opts.margin ?? 4
  const size = opts.size ?? 120
  const color = opts.color ?? '#000000'
  const background = opts.background ?? '#ffffff'

  const qr = QRCode.create(opts.content, { errorCorrectionLevel: ecl })
  const modules = qr.modules
  const n = modules.size
  const total = n + margin * 2

  // Armamos el path como una secuencia de "M x y h len" — un trazo
  // horizontal por cada run contiguo de módulos negros en cada fila.
  let path = ''
  for (let y = 0; y < n; y++) {
    let runStart = -1
    for (let x = 0; x < n; x++) {
      const filled = modules.get(x, y) === 1
      if (filled && runStart === -1) {
        runStart = x
      } else if (!filled && runStart !== -1) {
        path += `M${runStart + margin} ${y + margin + 0.5}h${x - runStart}`
        runStart = -1
      }
    }
    // Cerrar el último run si la fila termina en negro
    if (runStart !== -1) {
      path += `M${runStart + margin} ${y + margin + 0.5}h${n - runStart}`
    }
  }

  // viewBox en unidades de módulos · width/height en px CSS · stroke
  // de 1 unidad (un módulo) · crispEdges para que no antialias rompa
  // la nitidez al imprimir o renderizar a PNG vía expo-print.
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" ` +
    `width="${size}" height="${size}" shape-rendering="crispEdges">` +
    `<path fill="${background}" d="M0 0h${total}v${total}H0z"/>` +
    `<path stroke="${color}" d="${path}"/>` +
    `</svg>`
  )
}
